import { create, number, record, string, union, type } from 'superstruct';
/**
 * Payload for adding a single line item property
 */ export const AddLineItemPropertyPayload = type({
    variantId: number(),
    key: string(),
    value: union([
        string(),
        number()
    ])
});
/**
 * Payload for removing a single line item property
 */ export const RemoveLineItemPropertyPayload = type({
    variantId: number(),
    key: string()
});
/**
 * Payload for updating multiple properties at once
 */ export const UpdateLineItemPropertiesPayload = type({
    variantId: number(),
    properties: record(string(), union([
        string(),
        number()
    ]))
});
/**
 * Payload for clearing all properties
 */ export const ClearLineItemPropertiesPayload = type({
    variantId: number()
});
/**
 * Finds a cart line item by variant ID
 */ const findLineItemByVariantId = async (variantId)=>{
    const response = await fetch('/cart.js');
    if (!response.ok) {
        throw new Error('Failed to fetch cart');
    }
    const cart = await response.json();
    const lineItem = cart.items.find((item)=>item.variant_id === variantId);
    if (!lineItem) {
        return null;
    }
    return {
        key: lineItem.key,
        properties: lineItem.properties || {}
    };
};
/**
 * Updates a line item with new properties using Shopify's cart/change.js API
 */ const updateLineItem = async (lineItemKey, properties)=>{
    const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            id: lineItemKey,
            properties
        })
    });
    if (!response.ok) {
        const details = await response.json();
        throw new Error(details.description || details.message || 'Failed to update line item');
    }
    await response.json();
};
/**
 * Creates a function to add a property to a line item
 *
 * @example
 * // Add property with numeric value
 * await Context.addLineItemProperty({
 *   variantId: 12345678,
 *   key: 'Quantity Discount',
 *   value: 10 // Can be a number
 * })
 */ export const makeAddLineItemProperty = function() {
    return async function addLineItemProperty(rawPayload) {
        const { variantId , key , value  } = create(rawPayload, AddLineItemPropertyPayload);
        const lineItem = await findLineItemByVariantId(variantId);
        if (!lineItem) {
            throw new Error(`Line item with variant ID ${variantId} not found in cart`);
        }
        // Merge existing properties with the new property
        const updatedProperties = {
            ...lineItem.properties,
            [key]: value
        };
        await updateLineItem(lineItem.key, updatedProperties);
    };
};
/**
 * Creates a function to remove a property from a line item
 *
 * @example
 * // Remove a property from a line item
 * // This removes a custom property by setting it to an empty string
 * await Context.removeLineItemProperty({
 *   variantId: 12345678,
 *   key: 'Gift Wrap'
 * })
 */ export const makeRemoveLineItemProperty = function() {
    return async function removeLineItemProperty(rawPayload) {
        const { variantId , key  } = create(rawPayload, RemoveLineItemPropertyPayload);
        const lineItem = await findLineItemByVariantId(variantId);
        if (!lineItem) {
            throw new Error(`Line item with variant ID ${variantId} not found in cart`);
        }
        // Remove the property by deleting it from the properties object
        const updatedProperties = {
            ...lineItem.properties
        };
        delete updatedProperties[key];
        await updateLineItem(lineItem.key, updatedProperties);
    };
};
/**
 * Creates a function to update multiple properties at once
 *
 * @example
 * // Update multiple properties at once
 * // This is more efficient than calling addLineItemProperty multiple times
 * await Context.updateLineItemProperties({
 *   variantId: 12345678,
 *   properties: {
 *     'Gift Wrap': 'Yes',
 *     Monogram: 'ABC',
 *     'Gift Message': 'Happy Birthday!',
 *     'Special Instructions': 'Handle with care'
 *   }
 * })
 */ export const makeUpdateLineItemProperties = function() {
    return async function updateLineItemProperties(rawPayload) {
        const { variantId , properties  } = create(rawPayload, UpdateLineItemPropertiesPayload);
        const lineItem = await findLineItemByVariantId(variantId);
        if (!lineItem) {
            throw new Error(`Line item with variant ID ${variantId} not found in cart`);
        }
        // Merge existing properties with new properties
        const updatedProperties = {
            ...lineItem.properties,
            ...properties
        };
        await updateLineItem(lineItem.key, updatedProperties);
    };
};
